import { Avatar } from "@threadws/jute";
import type { CalendarEvent, CalendarPersona, CalendarSummary } from "../../api/generated";
import { UNASSIGNED_LANE } from "../../utils/dayLanes";

interface LaneHeaderProps {
  persona: CalendarPersona;
  calendars: CalendarSummary[];
  /** The persona's all-day events, already without hidden calendars. */
  allDay: CalendarEvent[];
  hidden: Set<string>;
  onToggle: (calendarId: string) => void;
  colorOf: (calendarId: string) => string;
}

/** The top of a persona's column: its name, a chip per calendar to show or hide it, and all-day events. */
export function LaneHeader({ persona, calendars, allDay, hidden, onToggle, colorOf }: LaneHeaderProps) {
  return (
    <div className="flex min-w-0 flex-col gap-2 border-l border-border-subtle px-2.5 py-3">
      <div className="flex items-center gap-2">
        <Avatar name={persona.name} size="sm" />
        <span className="truncate text-sm font-medium">{persona.name}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {calendars.length === 0 && (
          <span className="text-xs text-text-secondary">{persona.id === UNASSIGNED_LANE ? "Planned tasks with no persona" : "No calendars"}</span>
        )}
        {calendars.map((calendar) => {
          const shown = !hidden.has(calendar.id);
          const color = colorOf(calendar.id);
          return (
            <button
              key={calendar.id}
              type="button"
              aria-pressed={shown}
              title={`${shown ? "Hide" : "Show"} ${calendar.name} (${calendar.accountLabel})`}
              onClick={() => onToggle(calendar.id)}
              className="flex max-w-full cursor-pointer items-center gap-1.5 rounded-full border border-border-subtle bg-transparent px-2.5 py-1 font-body text-xs text-text-primary hover:border-border-strong"
              style={{ opacity: shown ? 1 : 0.5 }}
            >
              <span
                className="size-2 shrink-0 rounded-full border-[1.5px]"
                style={{ borderColor: color, background: shown ? color : "transparent" }}
              />
              <span className="truncate">{calendar.name}</span>
            </button>
          );
        })}
      </div>
      {allDay.map((event) => (
        <span
          key={event.id}
          title={event.summary}
          className="truncate rounded-sm px-2 py-0.5 text-xs"
          style={{ background: `color-mix(in oklab, ${colorOf(event.calendarId)} 16%, var(--surface-card))` }}
        >
          {event.summary || "(No title)"}
        </span>
      ))}
    </div>
  );
}
